import { getUserIdFromToken,  getSessionId,  setSessionId, isLoggedIn, generateSessionId, getGoalLevel } from "@/utils/storage";
import { isDeliberateTrial, planSessionBootstrap } from "@/utils/examMode";
import { getToken } from "@/utils/storage";
import { startGuestExam, startPaidExam , getStoredGuestExamUser} from "@/api/exam";
import { playTrackedLiveExamAudio } from "@/lib/speaking/exam/live/liveExamRuntimeAudio";
import {
  getLiveExamAudioEpoch,
  invokeUnlessLiveExamExiting,
  isLiveExamRuntimeExiting,
  shouldStartLiveExamAudio,
  trackLiveExamBufferSource,
  untrackLiveExamBufferSource,
} from "@/lib/speaking/exam/live/liveExamRuntimeTeardown";
import { getStaticAudioByType } from "../config/staticAudio";
import { Task1WsAuthPolicy } from "./task1WsAuthPolicy";
import {
  computeLatencyMs,
  markSpeakingLatency,
} from "@/lib/speaking/exam/live/speakingLatencyTelemetry";
import {
  task1GenerateQuestionDelayMs,
  TASK1_TRANSCRIPT_MISSING_MAX_RETRIES,
  TASK1_TRANSCRIPT_MISSING_RETRY_MS,
} from "@/lib/speaking/exam/live/task1SilencePolicy";
import LogRocket from "logrocket";
export interface AudioServiceConfig {
  websocketUrl: string;
  sessionId?: string;
  // When true, assistant_spoken (wasInterrupted: false) will be buffered and only
  // sent when explicitly flushed. This is used by Task 1 to avoid premature
  // sending when AI starts speaking but is then interrupted.
  delayAssistantSpoken?: boolean;
}
declare global {
  interface Window {
    __waitingForEndSound?: boolean;
    navigateToDashboard?: () => void;
  }
}

interface UserData {
  id: string;
  email: string;
  role: string;
  is_anonymous?: boolean;
  app_metadata?: any;
  user_metadata?: any;
}

export interface AudioServiceCallbacks {
  onTranscriptUpdate?: (transcript: string) => void;
  onLiveTranscriptUpdate?: (transcript: string) => void;
  onAIResponse?: (response: { text: string; audio?: string }) => void;
  onStatusChange?: (status: string) => void;
  onConnectionChange?: (connected: boolean) => void;
  onError?: (error: string) => void;
  onAIQuestionDisplay?: (question: string) => void;
  onAIQuestionHide?: () => void;
  onAIQuestionComplete?: (question: string) => void;
  onSilenceDetected?: () => void;
  onUserSpeaking?: () => void;
  onUserStoppedSpeaking?: () => void;
  onMicActivate?: () => void;
  onMicDeactivate?: () => void;
  onEndMessageFinished?: () => void; // New callback for when end message finishes
  onTaskSaved?: (taskId: string) => void; // New callback for when task is saved to database
  onTaskEnded?: () => void; // New callback for when task ends (silence timer)
}

class AudioService {
  private websocket: WebSocket | null = null;
  private mediaRecorder: MediaRecorder | null = null;
  private audioStream: MediaStream | null = null;
  private isRecording = false;
  private sessionId: string | null = null;
  private config: AudioServiceConfig;
  private callbacks: AudioServiceCallbacks;
  private reconnectAttempts = 0;
  private shouldReconnect = true;
  private reconnectTimer: NodeJS.Timeout | null = null;
  private wsAuthPolicy = new Task1WsAuthPolicy();
  private fullTranscript = "";
  private liveTranscript = "";
  private initialConnectionAttempts = 0;
  private MAX_INITIAL_CONNECTION_ATTEMPTS = 5;
  private lastWebSocketActivity = 0;
  private webSocketHealthCheckInterval: NodeJS.Timeout | null = null;
  private pendingRequests = new Map<string, { timestamp: number; type: string }>();

  private silenceTimer: NodeJS.Timeout | null = null;
  private lastSpeechTime = 0;
  private isAISpeaking = false;
  private aiResponsePending = false;
  private questionInFlight = false;
  private questionCount = 0; // number of AI questions that have been completed or interrupted
  private MAX_QUESTIONS = 2;
  private userSpokeSinceLastAI = false; // track whether user has spoken since last AI question
  private suppressAIResponsesUntil = 0;
  private aiGenerationWatchdogTimer: NodeJS.Timeout | null = null;
  private aiGenerationAttempts = 0;
  private MAX_AI_GENERATION_ATTEMPTS = 2;
  private currentAudioElement: HTMLAudioElement | null = null;
  private remainingSeconds = 120;
  private endMessageSpoken = false;
  private endingTriggered: boolean = false;
  private lastPhaseTimeoutId: NodeJS.Timeout | null = null;
  private currentAIText: string = "";
  private aiStartTime: number = 0;
  private userHasEverSpoken = false;
  private taskEnded = false; // Flag to block AI responses after task completion
  private isPlayingStaticMessage = false;
  private currentStaticMessageType: string | null = null;
  private isProtectedStaticMessage = false;
  private speechRecognitionActive = false;
  private audioContext: AudioContext | null = null;
  private currentAudioSource: AudioBufferSourceNode | null = null;
  private lastInterrupted: boolean = false;
  // Buffer a pending assistant_spoken payload when delayAssistantSpoken is enabled
  private pendingAssistantSpokenPayload: any = null;
  private cancelAllAI = false;
  private recordedChunks: Blob[] = [];
  private hasStopped = false;
  private currentRequestId: string | null = null; // Track current request to prevent duplicates
  private t1LastFinalTranscriptMs = 0;
  private t1SilenceGateElapsedMs = 0;
  private t1GenerateQuestionSentMs = 0;
  private transcriptMissingRetries = 0;

  constructor(config: AudioServiceConfig, callbacks: AudioServiceCallbacks = {}) {
    this.config = config;
    this.callbacks = callbacks;
  }

  private identifySessionInLogRocket(sessionId: string, isPaidUser: boolean): void {
    try {
      if (isPaidUser) {
        const storedUser = localStorage.getItem("user_data");
        let parsedUser: UserData | null = null;
        if (storedUser) {
          try {
            parsedUser = JSON.parse(storedUser) as UserData;
          } catch (parseError) {
            console.warn("⚠️ Failed to parse user_data for LogRocket identify:", parseError);
          }
        }

        const email =
          parsedUser?.email ||
          parsedUser?.user_metadata?.email ||
          "Unknown";
        const userId = parsedUser?.id || "paid-user-unknown";

        LogRocket.identify(userId, {
          name: email !== "Unknown" ? email.split("@")[0] : "Unknown User",
          email,
          userEmail: email,
          role: parsedUser?.role || "paid",
          subscriptionType: "paid",
          examId: sessionId,
        });

        LogRocket.track("Exam Started", {
          examId: sessionId,
          email,
          userType: "paid",
        });
        return;
      }

      const guestUser = getStoredGuestExamUser();
      const guestExamUserRaw = localStorage.getItem("guestExamUser");
      const guestExamUserParsed = guestExamUserRaw ? JSON.parse(guestExamUserRaw) : null;
      const guestToken =
        guestExamUserParsed?.token ||
        localStorage.getItem("guestToken") ||
        null;

      let guestTokenUserId: string | null = null;
      if (guestToken) {
        try {
          const parts = guestToken.split(".");
          if (parts.length === 3) {
            const payload = parts[1];
            const padded = payload + "=".repeat((4 - (payload.length % 4)) % 4);
            const json = atob(padded.replace(/-/g, "+").replace(/_/g, "/"));
            const data = JSON.parse(json);
            guestTokenUserId = data.sub || data.user_id || data.id || null;
          }
        } catch (decodeError) {
          console.warn("⚠️ Failed to decode guest token for LogRocket identify:", decodeError);
        }
      }

      const guestId =
        guestUser?.user_id ||
        guestExamUserParsed?.user_id ||
        localStorage.getItem("guest_id") ||
        guestTokenUserId ||
        "guest-user-unknown";

      LogRocket.identify(guestId, {
        name: "Guest User",
        role: "guest",
        subscriptionType: "guest",
        userId: guestId,
        examId: sessionId,
      });

      LogRocket.track("Exam Started", {
        examId: sessionId,
        userId: guestId,
        userType: "guest",
      });
    } catch (error) {
      console.warn("⚠️ Failed to sync LogRocket exam identity:", error);
    }
  }

  // ... existing code ...

  // setRemainingSeconds(seconds: number) {
  //   this.remainingSeconds = seconds;

  //   if (this.remainingSeconds <= 30) {
  //     console.log("⏳ Last 30 seconds: canceling AI generation and responses");
  //     this.aiResponsePending = false;
  //     this.questionInFlight = false;
  //     this.clearAIGenerationWatchdog();
  //     this.clearSilenceTimer();
  //     this.cancelAIAudio();
  //     this.callbacks.onAIQuestionHide?.();
  //   }
  // }

  public setUserHasSpokenFirst(hasSpoken: boolean) {
    this.userHasEverSpoken = hasSpoken;
    console.log(`🎯 User has spoken first flag set to: ${hasSpoken}`);
  }

  public async unlockAudio() {
    if (!this.audioContext) {
      this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    }
    if (this.audioContext.state === "suspended") {
      await this.audioContext.resume();
    }
    // console.log("🔓 AudioContext unlocked for Safari iOS");
  }

  private async playBase64Audio(base64Audio: string, aiText?: string, onEnd?: () => void) {
    if (this.cancelAllAI || isLiveExamRuntimeExiting() || !shouldStartLiveExamAudio()) {
      console.log('🔕 AI audio is disabled - skipping playback');
      return;
    }
    const epoch = getLiveExamAudioEpoch();
    try {
      if (!this.audioContext) {
        this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      }
      if (this.audioContext.state === "suspended") {
        await this.audioContext.resume();
      }

      const audioData = Uint8Array.from(atob(base64Audio), c => c.charCodeAt(0)).buffer;
      const buffer = await this.audioContext.decodeAudioData(audioData);
      const decodeReadyMs = Date.now();
      if (!this.isPlayingStaticMessage && aiText) {
        markSpeakingLatency({
          task: 'task1',
          marker: 'T1_AUDIO_DECODE_READY',
          sessionId: this.sessionId ?? undefined,
          metrics: {
            audio_startup_ms: computeLatencyMs(this.t1GenerateQuestionSentMs, decodeReadyMs),
          },
        });
      }
      if (this.cancelAllAI || isLiveExamRuntimeExiting() || epoch !== getLiveExamAudioEpoch()) {
        return;
      }

      const source = this.audioContext.createBufferSource();
      source.buffer = buffer;
      source.connect(this.audioContext.destination);

      // Track the current source for interruption
      this.currentAudioSource = source;
      if (!trackLiveExamBufferSource(source)) {
        this.currentAudioSource = null;
        return;
      }

      source.onended = () => {
        untrackLiveExamBufferSource(source);
        console.log("✅ Audio finished playing (Safari compatible)");
        // If we recently interrupted playback via cancelAIAudio(), stop() will
        // trigger this onended handler — ignore it because cancellation already
        // handled sending an interrupted event and UI updates.
        if (this.lastInterrupted) {
          console.log('⚠️ Audio ended after interruption - ignoring onended logic');
          this.lastInterrupted = false;
          this.currentAudioSource = null;
          this.aiStartTime = 0;
          this.isAISpeaking = false;
          this.aiResponsePending = false;
          invokeUnlessLiveExamExiting(onEnd, epoch);
          return;
        }
        this.isAISpeaking = false;
        this.aiResponsePending = false;
        this.currentAudioElement = null;
        const endTime = Date.now();
        const duration = endTime - this.aiStartTime;
        this.aiStartTime = 0;
        this.currentAudioSource = null; // <-- clear reference
        
        // Only send assistant_spoken for non-static messages (regular AI responses)
        if (aiText && !this.isPlayingStaticMessage) {
          const payload = {
            type: 'assistant_spoken',
            text: aiText,
            wasInterrupted: false,
            timestamp: endTime,
            duration: duration
          };

          // If configured to delay sending (Task 1), buffer the payload and let the
          // page (TaskOne) explicitly flush it when it confirms the UI has seen the
          // completed question. Otherwise send immediately as before.
          if (this.config && this.config.delayAssistantSpoken) {
            this.pendingAssistantSpokenPayload = payload;
            console.log('🧾 Buffered assistant_spoken payload (delay mode)');
          } else {
            this.sendWebSocketMessage(payload);
            console.log('📤 assistant_spoken sent immediately (normal mode)');
          }

          // Count this as a completed question (counts only fully-played audio)
          this.questionCount = Math.min(this.questionCount + 1, this.MAX_QUESTIONS);
          console.log(`📈 AI question completed. Count: ${this.questionCount}/${this.MAX_QUESTIONS}`);
          // Notify UI that the question has completed (so it can clear any active display)
          this.callbacks.onAIQuestionComplete?.(aiText);
        }
        
        invokeUnlessLiveExamExiting(onEnd, epoch);
      };

      if (this.cancelAllAI || isLiveExamRuntimeExiting() || epoch !== getLiveExamAudioEpoch()) {
        console.log('🔕 AI audio disabled after setup - aborting start');
        try { source.disconnect(); } catch {}
        untrackLiveExamBufferSource(source);
        this.currentAudioSource = null;
        return;
      }
      source.start(0);
      if (!this.isPlayingStaticMessage && aiText) {
        const playbackStartedMs = Date.now();
        markSpeakingLatency({
          task: 'task1',
          marker: 'T1_AUDIO_PLAYBACK_STARTED',
          sessionId: this.sessionId ?? undefined,
          metrics: {
            audio_startup_ms: computeLatencyMs(decodeReadyMs, playbackStartedMs),
            total_speech_end_to_audible_ms: computeLatencyMs(
              this.t1LastFinalTranscriptMs,
              playbackStartedMs,
            ),
          },
        });
      }
      this.isAISpeaking = true;
      this.aiResponsePending = true;
      this.aiStartTime = Date.now();
      this.callbacks.onStatusChange?.("AI is speaking...");

    } catch (error) {
      console.error("❌ Safari TTS playback error:", error);
      this.isAISpeaking = false;
      this.aiResponsePending = false;
      this.aiStartTime = 0;
      this.currentAudioSource = null; // <-- clear reference
    }
  }

  public enableAIAudio(): void {
    this.cancelAllAI = false;
  }

  // Permanently disable further AI audio playback and stop any current audio
  public disableAIAudio(): void {
    this.cancelAllAI = true;
    this.cancelAIAudio();
  }

  // Immediately terminate all AI activity and prepare for task completion
  public finishTaskImmediately(): void {
    console.log("🚨 FINISH TASK: Immediately terminating all AI activity");

    // Mark task as ended to block any further AI responses
    this.taskEnded = true;

    // Cancel any pending AI generation requests
    this.questionInFlight = false;
    this.aiResponsePending = false;
    this.clearAIGenerationWatchdog();

    // Cancel any playing AI audio immediately
    this.cancelAIAudio();

    // Clear all timers to prevent new AI requests
    this.clearSilenceTimer();
    this.clearLastPhaseTimeout();

    // Hide any displayed AI questions
    this.callbacks.onAIQuestionHide?.();

    // Reset AI-related states
    this.isAISpeaking = false;
    this.aiResponsePending = false;
    this.questionInFlight = false;
    this.isPlayingStaticMessage = false;
    this.currentStaticMessageType = null;
    this.isProtectedStaticMessage = false;

    console.log("✅ All AI activity terminated - ready for end audio");
  }

  setRemainingSeconds(seconds: number) {
    this.remainingSeconds = seconds;

    if (this.remainingSeconds < 30 && !this.endingTriggered) {
      console.log("⏳ Last 30 seconds: canceling AI generation but keeping voice detection active");
      // this.aiResponsePending = false;
      this.questionInFlight = false;
      this.clearAIGenerationWatchdog();
      // DON'T clear silence timer - keep voice detection active
      // this.clearSilenceTimer();
      // this.cancelAIAudio();
      this.callbacks.onAIQuestionHide?.();

      // Block AI questions in last 30 seconds
      this.endingTriggered = true;

      // Start silence timer only if user is not speaking
      if (!this.isAISpeaking) {
        this.startLastPhaseTimeout();
      }
    }
  }

  // Guarded end trigger to prevent duplicate end audio
  private endTriggeredSource: string | null = null;
  public triggerEndOnce(source: string = "unknown"): void {
    if (isLiveExamRuntimeExiting()) {
      return;
    }
    if (this.endMessageSpoken) {
      // console.log(`⚠️ End already triggered (source was: ${this.endTriggeredSource}), ignoring new trigger from: ${source}`);
      return;
    }
    this.endMessageSpoken = true;
    this.endTriggeredSource = source;
    window.__waitingForEndSound = true;

    // Immediately terminate all AI activity before playing end audio
    this.finishTaskImmediately();

    // Use local static audio instead of backend for silence-triggered endings
    this.playLocalEndAudio();
  }

  sendStaticMessage(messageType: string): void {
    // console.log(`📤 Sending static message: ${messageType}`);
    this.sendWebSocketMessage({
      type: 'static_message',
      messageType: messageType,
      timestamp: Date.now()
    });
  }

  private playLocalEndAudio(): void {
    if (isLiveExamRuntimeExiting()) {
      return;
    }
    console.log("🔊 Playing local end audio for silence-triggered ending");

    // Note: taskEnded is already set by finishTaskImmediately() in triggerEndOnce()

    // Notify that task has ended
    this.callbacks.onTaskEnded?.();

    // Stop recording immediately
    this.stopRecording();

    // Get end audio from centralized config
    const endAudio = getStaticAudioByType('end', 'task1');
    if (!endAudio) {
      console.error("❌ End audio not found in config");
      return;
    }

    // Use WebAudio for better control
    this.playStaticAudioUrl(endAudio.url, () => {
      console.log("✅ Local end audio finished - calling onEndMessageFinished callback");
      window.__waitingForEndSound = false;


      // Send save_task to backend
    // this.sendWebSocketMessage({ type: "save_task" });
    // console.log("📤 Sent save_task to backend");
      // Call the callback for end message finished
      this.callbacks.onEndMessageFinished?.();
    });
  }

  private playStaticAudioUrl(url: string, onEnd?: () => void): void {
    if (isLiveExamRuntimeExiting()) {
      return;
    }
    void playTrackedLiveExamAudio({
      url,
      audioContext: (window as Window & { __appAudioContext?: AudioContext }).__appAudioContext,
      onEnd,
    });
  }
  pickMime(): string {
    const candidates = [
      "audio/webm;codecs=opus",
      "audio/webm",
      "audio/mp4",   // Safari/iOS often records mp4/m4a
      "audio/mpeg",  // mp3 on some Chromium builds
      "audio/ogg"    // Firefox
    ];
    for (const t of candidates) {
      if ((window as any).MediaRecorder?.isTypeSupported?.(t)) return t;
    }
    return ""; // let the browser pick
  }

  async testConnection(): Promise<boolean> {
    try {
      console.log('🧪 Testing WebSocket connection with retry logic...');
      const success = await this.connectWebSocketWithRetry();
      if (success) {
        console.log('✅ WebSocket connection test successful!');
      } else {
        console.log('❌ WebSocket connection test failed after all retry attempts');
      }
      return success;
    } catch (error) {
      console.error('❌ WebSocket connection test failed:', error);
      return false;
    }
  }

  getConnectionStatus(): { connected: boolean; readyState: number | null } {
    return {
      connected: this.websocket?.readyState === WebSocket.OPEN,
      readyState: this.websocket?.readyState || null
    };
  }

  getMediaStream(): MediaStream | null {
    return this.audioStream;
  }

  async initialize(): Promise<boolean> {
    try {
      // Mobile-optimized microphone access
      const constraints = {
        audio: {
          sampleRate: 16000,
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          // Mobile-specific constraints
          latency: 0.01, // Low latency for mobile
          volume: 1.0
        }
      };
      
      this.audioStream = await navigator.mediaDevices.getUserMedia(constraints);
      
      console.log("🎤 AudioService: MediaStream created");
      console.log("🎤 AudioService: MediaStream ID:", this.audioStream.id);
      console.log("🎤 AudioService: MediaStream active:", this.audioStream.active);
      console.log("🎤 AudioService: MediaStream tracks:", this.audioStream.getTracks().map(t => ({
        kind: t.kind,
        enabled: t.enabled,
        readyState: t.readyState,
        id: t.id,
        label: t.label
      })));
      
      // Keep microphone stream alive on mobile
      if (this.audioStream) {
        this.audioStream.getTracks().forEach(track => {
          track.enabled = true;
          // Prevent track from being stopped by browser
          const originalStop = track.stop;
          track.stop = function() {
            console.log("🚫 Attempted to stop microphone track - preventing");
            // Don't actually stop the track
          };
        });
      }
      
      this.callbacks.onStatusChange?.("🎙️ Microphone access granted (mobile optimized)");
      return true;
    } catch (error) {
      console.error('Failed to access microphone:', error);
      this.callbacks.onError?.("❌ Microphone access denied");
      return false;
    }
  }

  private finalizeAndClose = () => {
    if (this.websocket && this.websocket.readyState === WebSocket.OPEN) {
      this.websocket.send(JSON.stringify({
        type: 'end_session',
        sessionId: this.sessionId,
        timestamp: Date.now(),
        finalTranscript: this.fullTranscript
      }));
      setTimeout(() => {
        try { this.websocket?.close(1000, 'Session ended'); } catch {}
        this.websocket = null;
      }, 500);
    }
    this.callbacks.onStatusChange?.("🛑 Recording session completed");
  };

  

  async startRecording(): Promise<boolean> {
    if (!this.audioStream) {
      const initialized = await this.initialize();
      if (!initialized) return false;
    }

    try {
      // Initialize session based on user type
      if (!this.sessionId) {
        await this.initializeSession();
      } else {
        // If session is already provided, still ensure LogRocket identity has exam context.
        this.identifySessionInLogRocket(this.sessionId, isLoggedIn());
      }

      // Use retry logic for initial WebSocket connection
      const connectionSuccess = await this.connectWebSocketWithRetry();
      if (!connectionSuccess) {
        console.error('❌ Failed to establish initial WebSocket connection after all retry attempts');
        this.callbacks.onError?.("❌ Failed to connect to backend. Please try again.");
        return false;
      }

      this.isRecording = true;
      // Reset task ended flag when starting a new recording session
      this.taskEnded = false;
  // Reset question counter at the start of a recording session
  this.questionCount = 0;
  this.questionInFlight = false;
      this.callbacks.onStatusChange?.("🎙️ Recording started! Start speaking...");

      // Start initial silence timer immediately
      setTimeout(() => {
        if (this.isRecording && !this.isAISpeaking && !this.aiResponsePending) {
          this.askQuestionAfterSilence();
        }
      }, 50); // Reduced to 50ms for near-instant response

      return true;
    } catch (error) {
      console.error('Failed to start recording:', error);
      this.callbacks.onError?.("❌ Failed to start recording");
      return false;
    }
  }

  async initializeSession(): Promise<void> {
    try {
      const currentExamId = localStorage.getItem('current_exam_id');
      const bootstrapPlan = planSessionBootstrap({
        isDeliberateTrial: isDeliberateTrial(),
        isLoggedIn: isLoggedIn(),
        existingSessionId: getSessionId(),
        currentExamId,
      });

      if (bootstrapPlan.action === 'reuse') {
        this.sessionId = bootstrapPlan.sessionId;
        this.identifySessionInLogRocket(this.sessionId, isLoggedIn() && !isDeliberateTrial());
        if (currentExamId) {
          console.log('🔄 RETRY MODE - Using existing session ID, no new session creation needed');
        } else {
          console.log('✅ Using existing sessionId for Task 1:', this.sessionId);
        }
        return;
      }

      if (bootstrapPlan.action === 'startPaidExam') {
        console.log('🔐 Initializing paid user session...');
        const goalLevel = getGoalLevel();
        this.sessionId = 'exam_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
        setSessionId(this.sessionId);
        console.log('🎯 Generated new session ID in frontend:', this.sessionId);

        startPaidExam({
          goal_level: goalLevel ?? null,
          session_id: this.sessionId,
        });

        this.identifySessionInLogRocket(this.sessionId, true);
        console.log('✅ New paid user session created:', this.sessionId);
        return;
      }

      console.log('👤 Initializing guest user session...');
      const response = await startGuestExam();
      console.log('Guest exam response:', response);
      this.sessionId = response.last_session_id;
      setSessionId(this.sessionId);
      this.identifySessionInLogRocket(this.sessionId, false);
      console.log('✅ Guest user session initialized:', this.sessionId, 'with user_id:', response.user_id);
    } catch (error) {
      console.error('❌ Failed to initialize session:', error);
      throw error;
    }
  }

  stopRecording(): void {

     if (this.hasStopped) return; // Prevent duplicate calls
    this.hasStopped = true;

    // Mark task as ended to block any further AI responses
    this.taskEnded = true;

    this.isRecording = false;
    this.shouldReconnect = false;
    this.clearSilenceTimer();
    this.clearLastPhaseTimeout();
    this.clearAIGenerationWatchdog();
    this.cancelAIAudio();

    // if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
    //   this.mediaRecorder.stop();
    //   this.mediaRecorder = null;
    // }

    if (this.audioStream) {
      this.audioStream.getTracks().forEach(track => {
        try {
          // Bypass any instance-level override of stop() by calling the
          // prototype implementation directly. Some mobile-optimized code
          // replaces track.stop to prevent stopping; this forces a real stop.
          (MediaStreamTrack.prototype as any).stop.call(track);
        } catch (e) {
          try { track.stop(); } catch {}
        }
      });
      this.audioStream = null;
    }

    // Do NOT close the global audio context here.
    // It must remain active for the next task (Task 2/3) to work on iOS without a new user gesture.
    // The context will be explicitly closed only when the user exits the exam via cleanupGlobalAudio().
    /*
    try {
      const ctx: AudioContext | undefined = (window as any).__appAudioContext;
      if (ctx && typeof ctx.close === 'function') {
        ctx.close().catch(() => {});
        (window as any).__appAudioContext = undefined;
      }
    } catch (e) {
      console.warn('Error closing global audio context during stopRecording:', e);
    }
    */

    // if (this.websocket && this.websocket.readyState === WebSocket.OPEN) {
    //   this.websocket.send(JSON.stringify({
    //     type: 'end_session',
    //     sessionId: this.sessionId,
    //     timestamp: Date.now(),
    //     finalTranscript: this.fullTranscript
    //   }));

    //   setTimeout(() => {
    //     if (this.websocket) {
    //       this.websocket.close(1000, 'Session ended');
    //       this.websocket = null;
    //     }
    //   }, 1000);
    // }

    // Check if this is a retry attempt
    const isRetryAttempt = localStorage.getItem('current_task_attempt_id') !== null;

    this.sendWebSocketMessage({
      type: "save_task",
      isRetryAttempt: isRetryAttempt
    });
    console.log("📤 Sent save_task to backend from stopRecording", { isRetryAttempt });

    this.callbacks.onStatusChange?.("🛑 Recording session completed");
  }

  private clearSilenceTimer() {
    if (this.silenceTimer) {
      clearTimeout(this.silenceTimer);
      this.silenceTimer = null;
    }
  }

  private schedulePostSpeechSilenceTimer(source: string) {
    this.clearSilenceTimer();
    const delayMs = task1GenerateQuestionDelayMs();
    console.log(`🔇 ${source} - scheduling ${delayMs}ms silence timer for next question`);
    this.silenceTimer = setTimeout(() => {
      this.t1SilenceGateElapsedMs = Date.now();
      markSpeakingLatency({
        task: 'task1',
        marker: 'T1_SILENCE_GATE_ELAPSED',
        sessionId: this.sessionId ?? undefined,
        metrics: {
          client_wait_ms: computeLatencyMs(this.t1LastFinalTranscriptMs, this.t1SilenceGateElapsedMs),
        },
      });
      if (this.isRecording && !this.questionInFlight) {
        if (this.remainingSeconds >= 30) {
          console.log("⏰ Silence timer triggered - asking question after silence");
          this.askQuestionAfterSilence();
        } else {
          console.log("⏰ Silence timer triggered - in last 30 seconds, keeping voice detection active");
        }
      }
    }, delayMs);
  }

  private scheduleTranscriptMissingRetry() {
    if (this.transcriptMissingRetries >= TASK1_TRANSCRIPT_MISSING_MAX_RETRIES) {
      return;
    }
    this.transcriptMissingRetries += 1;
    console.log(
      `⏳ Transcript not ready on backend — retry ${this.transcriptMissingRetries}/${TASK1_TRANSCRIPT_MISSING_MAX_RETRIES} in ${TASK1_TRANSCRIPT_MISSING_RETRY_MS}ms`,
    );
    setTimeout(() => {
      if (this.isRecording && !this.questionInFlight && !this.aiResponsePending) {
        this.askQuestionAfterSilence();
      }
    }, TASK1_TRANSCRIPT_MISSING_RETRY_MS);
  }

  private clearAIGenerationWatchdog() {
    if (this.aiGenerationWatchdogTimer) {
      console.log("🛑 Clearing AI generation watchdog timer");
      clearTimeout(this.aiGenerationWatchdogTimer);
      this.aiGenerationWatchdogTimer = null;
    }
  }

  private clearLastPhaseTimeout() {
    if (this.lastPhaseTimeoutId) {
      clearTimeout(this.lastPhaseTimeoutId);
      this.lastPhaseTimeoutId = null;
    }
  }

  private startLastPhaseTimeout() {
    this.clearLastPhaseTimeout();
    // console.log("⏳ Starting 5-second silence timer for last 30 seconds phase");
    this.lastPhaseTimeoutId = setTimeout(() => {
      // Only trigger final end if we're in the last 30s AND there is more than
      // 5 seconds remaining. If remainingSeconds is 5 or less, the silence
      // should not force an end because the task is nearly complete.
      if (
        this.remainingSeconds < 30 &&
        this.remainingSeconds > 6 &&
        this.isRecording &&
        !this.isAISpeaking &&
        !this.endMessageSpoken
      ) {
        // console.log("🚨 Silence >5s in last 30s: Sending final END message to backend");
        this.triggerEndOnce("last_phase_silence");
        return;
      }
    }, 7000);
  }

  // Method to check if user has never spoken and handle early ending
  private checkUserSilenceAndEnd() {
    if (!this.userHasEverSpoken && this.remainingSeconds < 30 && this.isRecording && !this.isAISpeaking && !this.isPlayingStaticMessage && !this.endMessageSpoken) {
      // console.log("🚨 User has never spoken and we're in last 30 seconds - ending task");
      this.endMessageSpoken = true;
      window.__waitingForEndSound = true;
      this.sendStaticMessage("end");
      return true;
    }
    return false;
  }

  private startAIGenerationWatchdog() {
    this.clearAIGenerationWatchdog();
    this.aiGenerationAttempts = Math.min(this.aiGenerationAttempts + 1, this.MAX_AI_GENERATION_ATTEMPTS);
    console.log(`⏰ Starting AI generation watchdog (attempt ${this.aiGenerationAttempts}/${this.MAX_AI_GENERATION_ATTEMPTS})`);
    this.aiGenerationWatchdogTimer = setTimeout(() => {
      if (!this.isRecording) {
        console.log('🛑 Watchdog: not recording, skipping');
        return;
      }
      if (!this.aiResponsePending && !this.questionInFlight) {
        console.log('🛑 Watchdog: no pending response or question in flight, skipping');
        return;
      }
      if (!this.websocket || this.websocket.readyState !== WebSocket.OPEN) {
        console.log('🛑 Watchdog: websocket not open, skipping');
        return;
      }
      if (this.aiGenerationAttempts <= this.MAX_AI_GENERATION_ATTEMPTS) {
        console.log('⚠️ Watchdog: resending generate_question due to timeout');
        // Generate new request ID for watchdog resend
        const newRequestId = `rq_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
        this.currentRequestId = newRequestId;
        console.log(`📤 Watchdog resending with new request ID: ${newRequestId}`);
        this.sendWebSocketMessage({ 
          type: 'generate_question', 
          requestId: newRequestId,
          timestamp: Date.now() 
        });
        this.startAIGenerationWatchdog();
      } else {
        console.log('🛑 Watchdog: max attempts reached, giving up');
      }
    }, 5000);
  }

  private askQuestionAfterSilence() {
    // Block AI questions until user has spoken first
    if (!this.userHasEverSpoken) {
      console.log("🚫 BLOCKED: Not asking question until user speaks first");
      return;
    }

    // Task 1 safety: do not interrupt early thinking
    if (this.fullTranscript.length < 120) {
      console.log("🚫 BLOCKED: Transcript too short for AI question");
      return;
    }

    // Block AI questions in last 30 seconds
    if (this.remainingSeconds < 30) {
      console.log("🚫 BLOCKED: Not asking question in last 30 seconds");
      return;
    }

    // Block AI questions if we've already reached the allowed number
    if (this.questionCount >= this.MAX_QUESTIONS) {
      console.log(`🚫 BLOCKED: Reached max AI questions (${this.questionCount}/${this.MAX_QUESTIONS})`);
      this.callbacks.onStatusChange?.("No more AI questions will be generated for this task.");
      return;
    }

    // Require user to have spoken since the last AI question (prevent back-to-back AI questions)
    if (!this.userSpokeSinceLastAI) {
      console.log("🚫 BLOCKED: Waiting for user to speak before generating another AI question");
      return;
    }

    // Prevent multiple questions from being generated simultaneously
    if (this.questionInFlight || this.aiResponsePending) {
      console.log("🚫 BLOCKED: Question already in flight or AI response pending");
      return;
    }

    if (this.websocket && this.websocket.readyState === WebSocket.OPEN && !this.endMessageSpoken) {
      console.log("🤖 Generating new question after silence...");
      this.callbacks.onStatusChange?.("AI is generating a question...");

      // Clear the silence timer since we're now generating a question
      this.clearSilenceTimer();

      this.aiResponsePending = true;
      this.questionInFlight = true;
      this.startAIGenerationWatchdog();

      const requestId = `rq_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
      this.currentRequestId = requestId; // Store current request ID
      console.log(`📤 Sending question request with ID: ${requestId}`);
      this.t1GenerateQuestionSentMs = markSpeakingLatency({
        task: 'task1',
        marker: 'T1_GENERATE_QUESTION_SENT',
        sessionId: this.sessionId ?? undefined,
        metrics: {
          client_wait_ms: computeLatencyMs(this.t1LastFinalTranscriptMs, Date.now()),
        },
      });
      const success = this.sendWebSocketMessage({
        type: 'generate_question',
        requestId,
        timestamp: Date.now()
      });

      if (!success) {
        this.aiResponsePending = false;
        this.questionInFlight = false;
      }
    }
  }

  handleUserSpeech(interimTranscript: string, finalTranscript: string) {
    // Check if we're playing a protected static message (start or end)
    if (this.isProtectedStaticMessage) {
      console.log(`🚫 User speech blocked - protected static message (${this.currentStaticMessageType}) is playing`);
      // Ignore user input during protected static messages
      return;
    }
    
    // ALWAYS prioritize user speech - even during AI responses (mobile fix)
    if (this.isPlayingStaticMessage || this.isAISpeaking || this.aiResponsePending) {
      console.log("⏹️ User spoke - prioritizing user and cancelling AI audio immediately (mobile optimized)");
      this.cancelAIAudio();
      
      // Force immediate response on mobile
      this.callbacks.onUserSpeaking?.();
      this.callbacks.onStatusChange?.("🗣️ User interrupting AI - listening...");
    }

    // Always cancel AI audio immediately on any user speech (for non-protected messages)
    if (interimTranscript) {
      this.cancelAIAudio(); // <-- Ensure AI audio is stopped instantly
      this.lastSpeechTime = Date.now();

      this.liveTranscript = interimTranscript;
      this.callbacks.onLiveTranscriptUpdate?.(interimTranscript);

      this.aiResponsePending = false;
      this.questionInFlight = false;
      this.suppressAIResponsesUntil = Date.now() + 3000;
      this.callbacks.onAIQuestionHide?.();

      // Clear all timers and reset state when user speaks to prevent duplicate questions
      this.clearSilenceTimer();
      this.clearAIGenerationWatchdog(); // <-- Clear watchdog to prevent duplicate questions
      this.currentRequestId = null; // <-- Clear current request ID
      if (this.remainingSeconds < 30 && this.isRecording) {
        // User is speaking, so cancel any ending timeout
        this.clearLastPhaseTimeout();
      }
      this.callbacks.onUserSpeaking?.();
    }

  if (finalTranscript) {
    // Mark that user has spoken at least once
    this.userHasEverSpoken = true;
    // Mark that user has spoken since last AI question
    this.userSpokeSinceLastAI = true;

    this.fullTranscript += finalTranscript;
    this.callbacks.onTranscriptUpdate?.(this.fullTranscript);

    const userId = getUserIdFromToken();
    const sessionId = getSessionId();
    const goalLevel = getGoalLevel();
    const guestUser = getStoredGuestExamUser();
    console.log("guestuser in handleUserSpeech", guestUser);
    // Check if this is a retry attempt
    const isRetryAttempt = localStorage.getItem('current_task_attempt_id') !== null;
    
    const payload = {
    type: 'transcript',
    data: finalTranscript.trim(),
    user_id: userId ?? guestUser?.user_id,
    session_id: sessionId,
    goal_level: goalLevel,
    task: 'task1',
    isRetryAttempt: isRetryAttempt,
    timestamp: Date.now(),
  };
  console.log("📤 Sending WebSocket message payload:", payload);

this.sendWebSocketMessage(payload);
    
    // Clear any existing timers before setting a new silence timer
      this.transcriptMissingRetries = 0;
      this.t1LastFinalTranscriptMs = markSpeakingLatency({
        task: 'task1',
        marker: 'T1_LAST_FINAL_TRANSCRIPT',
        sessionId: this.sessionId ?? undefined,
      });
      this.clearSilenceTimer();
      this.clearAIGenerationWatchdog(); // <-- Clear watchdog when user stops speaking
      this.schedulePostSpeechSilenceTimer('User stopped speaking');

    this.callbacks.onUserStoppedSpeaking?.();

    // Handle last phase timeout restart when user stops speaking
    if (this.remainingSeconds < 30 && this.isRecording && this.endingTriggered) {
      // User stopped speaking in last 30 seconds, restart the 5-second timeout
      this.startLastPhaseTimeout();
    }
  }
}
  public cancelAIAudio() {
    // Check if we're playing a protected static message
    if (this.isProtectedStaticMessage) {
      console.log(`🚫 Cancellation blocked - protected static message (${this.currentStaticMessageType}) must complete`);
      // Do not cancel protected static messages (including end messages)
      return;
    }
    
    console.log("🛑 Cancelling AI audio - user has priority");
    // Stop AudioContext playback if active
    if (this.currentAudioSource) {
      try {
        // Mark that the next onended should be treated as interruption
        this.lastInterrupted = true;
        this.currentAudioSource.stop();
        this.currentAudioSource.disconnect();
        this.currentAudioSource = null;
      } catch (error) {
        console.error('Error canceling AudioContext source:', error);
      }
    }

    // Old HTMLAudioElement logic (for fallback)
    if (this.currentAudioElement) {
      try {
        // Mark that the next onended should be treated as interruption
        this.lastInterrupted = true;
        this.currentAudioElement.pause();
        this.currentAudioElement.currentTime = 0;
        this.currentAudioElement.src = '';
        this.currentAudioElement.load();
        this.currentAudioElement = null;
      } catch (error) {
        console.error('Error canceling audio element:', error);
      }
    }

    // If we were playing a static message and user interrupted, send an interrupted message
    if (this.isPlayingStaticMessage || this.isAISpeaking) {
      const currentAIText = this.currentAIText || "AI audio";
      if (this.aiStartTime > 0) {
        // If we had buffered an assistant_spoken payload (delay mode), drop it and
        // instead send an interrupted assistant_spoken to ensure backend doesn't count it.
        if (this.pendingAssistantSpokenPayload) {
          console.log('🗑 Dropping buffered assistant_spoken payload due to interruption');
          this.pendingAssistantSpokenPayload = null;
        }

        this.sendWebSocketMessage({
          type: 'assistant_spoken',
          text: currentAIText,
          wasInterrupted: true,
          timestamp: Date.now(),
          duration: Date.now() - this.aiStartTime
        });
        // Interrupted questions are NOT counted toward the fully-played question limit
        console.log(`📉 AI question interrupted (not counted towards limit). Count remains: ${this.questionCount}/${this.MAX_QUESTIONS}`);
        // Hide the question from UI when interrupted
        this.callbacks.onAIQuestionHide?.();
      }
    }

    // Reset all AI-related states
    this.isAISpeaking = false;
    this.aiResponsePending = false;
    this.questionInFlight = false;
    this.isPlayingStaticMessage = false;
    this.currentStaticMessageType = null;
    this.isProtectedStaticMessage = false;
    this.clearSilenceTimer();
    this.callbacks.onStatusChange?.("User interrupted AI - listening...");

    // Re-enable microphone if it was disabled for static messages
    if (this.isPlayingStaticMessage) {
      this.callbacks.onMicActivate?.();
    }

    // Clear AI text tracking
    this.aiStartTime = 0;
    this.currentAIText = "";
  }

  // Public: flush any buffered assistant_spoken payload (used by Task 1 to confirm send)
  public flushBufferedAssistantSpoken(): void {
    if (this.pendingAssistantSpokenPayload) {
      this.sendWebSocketMessage(this.pendingAssistantSpokenPayload);
      console.log('📤 Flushed buffered assistant_spoken payload to backend');
      this.pendingAssistantSpokenPayload = null;
    } else {
      console.log('📭 No buffered assistant_spoken payload to flush');
    }
  }

  private async connectWebSocketWithRetry(): Promise<boolean> {
    this.initialConnectionAttempts = 0;
    
    while (this.initialConnectionAttempts < this.MAX_INITIAL_CONNECTION_ATTEMPTS) {
      this.initialConnectionAttempts++;
      
      try {
        console.log(`🔄 Initial WebSocket connection attempt ${this.initialConnectionAttempts}/${this.MAX_INITIAL_CONNECTION_ATTEMPTS}`);
        this.callbacks.onStatusChange?.(`Connecting to backend... (attempt ${this.initialConnectionAttempts}/${this.MAX_INITIAL_CONNECTION_ATTEMPTS})`);
        
        await this.connectWebSocket();
        console.log('✅ Initial WebSocket connection successful!');
        this.callbacks.onStatusChange?.("Connected to backend successfully!");
        return true;
      } catch (error) {
        console.error(`❌ Initial connection attempt ${this.initialConnectionAttempts} failed:`, error);
        
        if (this.initialConnectionAttempts >= this.MAX_INITIAL_CONNECTION_ATTEMPTS) {
          console.error('❌ All initial connection attempts failed. Giving up.');
          this.callbacks.onError?.("Failed to connect to backend after multiple attempts. Please check your internet connection and try again.");
          this.callbacks.onStatusChange?.("Connection failed - please try again");
          return false;
        }
        
        // Calculate delay for next attempt (exponential backoff)
        const delay = Math.min(1000 * Math.pow(2, this.initialConnectionAttempts - 1), 5000);
        console.log(`⏳ Waiting ${delay}ms before next connection attempt...`);
        this.callbacks.onStatusChange?.(`Connection failed, retrying in ${Math.round(delay/1000)}s... (attempt ${this.initialConnectionAttempts}/${this.MAX_INITIAL_CONNECTION_ATTEMPTS})`);
        
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
    
    return false;
  }

  private async connectWebSocket(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (this.wsAuthPolicy.isTerminal()) {
        console.warn('⛔ WebSocket connect aborted: auth session is terminal');
        reject(new Error('WebSocket auth session is terminal'));
        return;
      }

      if (this.websocket && this.websocket.readyState === WebSocket.OPEN) {
        console.log('WebSocket already connected');
        resolve();
        return;
      }

      console.log('Attempting to connect to WebSocket:', this.config.websocketUrl);
      this.callbacks.onConnectionChange?.(false);
      this.callbacks.onStatusChange?.("Connecting to backend...");

      this.websocket = new WebSocket(this.config.websocketUrl);

      this.websocket.onopen = () => {   
        console.log('Connection details:', {
          url: this.config.websocketUrl,
          sessionId: this.sessionId,
          readyState: this.websocket!.readyState
        });

        this.callbacks.onConnectionChange?.(true);
        this.callbacks.onStatusChange?.("Connected to backend");
        this.reconnectAttempts = 0;
        this.initialConnectionAttempts = 0; // Reset initial connection attempts on successful connection
        
        // Start WebSocket health monitoring
        this.startWebSocketHealthMonitoring();

        // Wait a bit for WebSocket to be fully ready before sending messages
        setTimeout(() => {
          if (this.websocket && this.websocket.readyState === WebSocket.OPEN) {
            const connectionMessage = {
              type: 'connection',
              sessionId: this.sessionId,
              timestamp: Date.now()
            };
            this.websocket.send(JSON.stringify(connectionMessage));
             
            // Send user auth token for RLS-enabled backend, if available
            try {
              let tokenToSend: string | null = null;
              if (isLoggedIn()) {
                tokenToSend = getToken() || null;
              }
              if (!tokenToSend) {
                // Fallback to guest token
                const guestExamUserRaw = localStorage.getItem('guestExamUser');
                const guestToken = guestExamUserRaw ? (JSON.parse(guestExamUserRaw)?.token ?? null) : (localStorage.getItem('guestToken'));
                tokenToSend = guestToken || null;
              }
              if (tokenToSend && this.wsAuthPolicy.canSendAuth()) {
                console.log('🔐 Sending auth token to backend for RLS access (paid or guest)');
                this.websocket.send(JSON.stringify({ type: 'auth', access_token: tokenToSend }));
                this.wsAuthPolicy.onAuthSent();
              }
            } catch (e) {
              console.warn('Auth token send failed:', e);
            }
          }
        }, 100); // Small delay to ensure WebSocket is fully ready

        resolve();
      };

      this.websocket.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          const timestamp = Date.now();
          console.log(`📥 [${new Date().toISOString()}] Received WebSocket message:`, message);
          
          // Update last activity
          this.lastWebSocketActivity = timestamp;
          
          // Track response for pending requests
          if (message.requestId && this.pendingRequests.has(message.requestId)) {
            const pendingRequest = this.pendingRequests.get(message.requestId);
            const responseTime = timestamp - pendingRequest!.timestamp;
            console.log(`📥 [${new Date().toISOString()}] Response received for ${message.requestId} (${pendingRequest!.type}) in ${responseTime}ms`);
            this.pendingRequests.delete(message.requestId);
          }
          
          this.handleWebSocketMessage(message);
        } catch (error) {
          console.error(`❌ [${new Date().toISOString()}] Failed to parse WebSocket message:`, error);
          console.error('Raw message data:', event.data);
        }
      };

      this.websocket.onclose = (event) => {
        console.log('WebSocket disconnected:', {
          code: event.code,
          reason: event.reason,
          wasClean: event.wasClean
        });
        this.callbacks.onConnectionChange?.(false);
        this.callbacks.onStatusChange?.(" Connection lost");

        if (this.shouldReconnect && this.isRecording) {
          console.log('🔄 Scheduling reconnection...');
          this.scheduleReconnect();
        }
      };

      this.websocket.onerror = (error) => {
        console.error('WebSocket connection error:', error);
        console.error('Error details:', {
          url: this.config.websocketUrl,
          readyState: this.websocket?.readyState,
          error: error
        });
        this.callbacks.onConnectionChange?.(false);
        this.callbacks.onStatusChange?.("Connection failed");
        reject(error);
      };

      setTimeout(() => {
        if (this.websocket!.readyState !== WebSocket.OPEN) {
          reject(new Error('WebSocket connection timeout'));
        }
      }, 10000);
    });
  }

  private handleWebSocketMessage(message: any): void {
    console.log(`🔍 Processing message type: ${message.type}`);

    switch (message.type) {
      case 'connection':
        console.log('Connected to conversation service');
        this.callbacks.onStatusChange?.("Backend connection established");
        break;

      case 'guest_id_updated':
        if (message.guest_id) {
          console.log('Received new guest_id from backend:', message.guest_id);
          // setGuestId(message.guest_id);
        }
        break;


      case 'static_message_response':
        const staticResponse = message.data;
        console.log("Static message response received:", staticResponse.messageType);
        if (isLiveExamRuntimeExiting()) break;

        if (staticResponse.audio) {
          this.aiStartTime = Date.now();

          // Only handle 'start' messages from backend - 'end' messages are now handled locally
          if (staticResponse.messageType === 'start') {
            // For start message, activate speech recognition after audio ends
            const onStartComplete = () => this.callbacks.onMicActivate?.();
            this.playStaticAudioResponse(staticResponse, onStartComplete);
          } else if (staticResponse.messageType === 'end') {
            // Skip backend end messages - we now use local static audio for silence-triggered endings
            console.log("⚠️ Backend end message received but using local static audio instead");
          }
        }
        break;

      case 'ai_response':
        // Block AI responses if task has ended
        if (this.taskEnded || isLiveExamRuntimeExiting()) {
          console.log("🚫 BLOCKED: Task has ended - ignoring AI response");
          this.aiResponsePending = false;
          this.questionInFlight = false;
          this.clearAIGenerationWatchdog();
          this.cancelAIAudio();
          this.callbacks.onAIQuestionHide?.();
          return;
        }
         if (
        this.remainingSeconds < 30 &&
        !this.isAISpeaking // Only block if AI is not already speaking
      ) {
        console.log("🚫 BLOCKED: Ignoring new AI response in last 30 seconds");
        this.aiResponsePending = false;
        this.questionInFlight = false;
        this.clearAIGenerationWatchdog();
        this.callbacks.onAIQuestionHide?.();
        return;
      }

        if (Date.now() < this.suppressAIResponsesUntil) {
          this.aiResponsePending = false;
          this.questionInFlight = false;
          this.callbacks.onAIQuestionHide?.();
          return;
        }

        const response = message.data;
        const timeSinceLastSpeech = Date.now() - this.lastSpeechTime;
        const userRecentlySpoke = timeSinceLastSpeech < 2000;

        if (userRecentlySpoke && this.aiResponsePending) {
          this.aiResponsePending = false;
          this.callbacks.onStatusChange?.("User interrupted AI - listening...");
          return;
        }

        // Check if this response matches our current request (prevents duplicate processing)
        if (this.currentRequestId && response.requestId && response.requestId !== this.currentRequestId) {
          console.log(`🚫 BLOCKED: Response requestId (${response.requestId}) doesn't match current (${this.currentRequestId}) - ignoring duplicate`);
          return;
        }

        //added
        this.sendWebSocketMessage({
          type: "ai_response_ack",
          sessionId: this.sessionId,
          requestId: response.requestId ?? null,
          
          timestamp: Date.now()
        });

        this.aiResponsePending = false;
        this.questionInFlight = false;
        this.aiGenerationAttempts = 0;
        this.clearAIGenerationWatchdog();
        this.currentRequestId = null; // Clear current request ID
        const responseReceivedMs = Date.now();
        markSpeakingLatency({
          task: 'task1',
          marker: 'T1_AI_RESPONSE_RECEIVED',
          sessionId: this.sessionId ?? undefined,
          metrics: {
            backend_response_ms: computeLatencyMs(this.t1GenerateQuestionSentMs, responseReceivedMs),
          },
        });
        console.log("🔍 AI response received:", response);
        // If max questions already reached, ignore new AI responses
        if (this.questionCount >= this.MAX_QUESTIONS) {
          console.log(`🚫 Max AI questions reached (${this.questionCount}/${this.MAX_QUESTIONS}) - ignoring AI response`);
          // Ensure any displayed question is hidden
          this.callbacks.onAIQuestionHide?.();
          return;
        }
        if (response.audio) {
          // Check if task has ended before playing AI audio
          if (this.taskEnded) {
            console.log("🚫 BLOCKED: Task has ended - not playing AI audio response");
            this.callbacks.onAIQuestionHide?.();
            return;
          }

          this.callbacks.onAIResponse?.(response);
          this.callbacks.onAIQuestionDisplay?.(response.text);

          this.currentAIText = response.text;
          this.aiStartTime = Date.now();
          // Reset the per-question user-speech flag: user must speak after this AI speaks
          this.userSpokeSinceLastAI = false;
          this.playAudioResponse(response);

        } else {
          this.callbacks.onAIQuestionHide?.();
        }
        break;

      case 'heartbeat':
        this.sendWebSocketMessage({
          type: 'heartbeat_ack',
          timestamp: Date.now()
        });
        break;

      case 'task_save_ack':
        console.log('✅ Task saved successfully:', message.task_id);
        this.callbacks.onTaskSaved?.(message.task_id);
        break;

      case 'error':
        console.error('Server error:', message.message);
        if (message.message === 'No transcript available for question generation') {
          this.callbacks.onStatusChange?.("Listening...");
          this.aiResponsePending = false;
          this.questionInFlight = false;
          this.clearAIGenerationWatchdog();
          this.scheduleTranscriptMissingRetry();
        } else if (message.message === 'Invalid access token') {
          const result = this.wsAuthPolicy.onInvalidAccessToken();
          if (!result.shouldNotifyError && !result.shouldClose) {
            break;
          }
          console.error('❌ Invalid access token - stopping auth (no resend)');
          this.shouldReconnect = false;
          this.clearReconnectTimer();
          if (result.shouldClose) {
            try {
              this.websocket?.close();
            } catch (e) {
              console.warn('WebSocket close after invalid auth failed:', e);
            }
          }
          if (result.shouldNotifyError) {
            this.callbacks.onStatusChange?.("Invalid access token");
            this.callbacks.onError?.("Invalid access token");
          }
        } else {
          this.callbacks.onStatusChange?.(` ${message.message}`);
        }
        break;
    }
  }


  private playStaticAudioResponse(response: any, onEnd?: () => void) {
    if (isLiveExamRuntimeExiting()) return;
    console.log("🔊 Playing static message audio - mic disabled");
    this.isPlayingStaticMessage = true;
    this.currentStaticMessageType = response.messageType;
    // Protect start and end messages from user interruption - end messages are ALWAYS protected
    this.isProtectedStaticMessage = (response.messageType === 'start' || response.messageType === 'end');

    if (this.isProtectedStaticMessage) {
      console.log(`🔒 Protected static message (${response.messageType}) - user interruption disabled`);
    }

    // Keep voice detection active during static messages - no need to deactivate mic
    // this.callbacks.onMicDeactivate?.();
    const base64Audio = response.audio;
    const aiText = response.text;
    try {
      // Only cancel AI audio if this is not a protected end message
      if (!this.isProtectedStaticMessage || response.messageType !== 'end') {
        this.cancelAIAudio();
      }
      this.currentAIText = aiText; // Track the AI text for interruption handling
      this.playBase64Audio(base64Audio, aiText, () => {
        this.isPlayingStaticMessage = false;
        this.currentStaticMessageType = null;
        this.isProtectedStaticMessage = false;
        this.isAISpeaking = false;
        this.aiResponsePending = false;
        this.currentAudioElement = null;
        this.aiStartTime = 0;
        console.log(`✅ Static message (${response.messageType}) finished - mic enabled`);
        if (onEnd) onEnd();
      });
      this.isAISpeaking = true;
      this.aiResponsePending = true;
      this.aiStartTime = Date.now();
      this.callbacks.onStatusChange?.("AI is speaking...");
    } catch (error) {
      console.error('Static audio playback error:', error);
      this.isAISpeaking = false;
      this.aiResponsePending = false;
      this.isPlayingStaticMessage = false;
      this.currentStaticMessageType = null;
      this.isProtectedStaticMessage = false;
      this.currentAudioElement = null;
      this.aiStartTime = 0;
      if (onEnd) onEnd();
    }
  }


  private playAudioResponse(response: any, onEnd?: () => void) {
    try {
      const base64Audio = response.audio;
      const aiText = response.text;

      // Check if task has ended before starting audio playback
      if (this.taskEnded || isLiveExamRuntimeExiting()) {
        console.log("🚫 BLOCKED: Task has ended - not playing AI audio response");
        this.callbacks.onAIQuestionHide?.();
        return;
      }

      this.cancelAIAudio();
      this.playBase64Audio(base64Audio, aiText, () => {
        this.callbacks.onStatusChange?.("Listening...");
        this.isAISpeaking = false;
        this.aiResponsePending = false;
        this.currentAudioElement = null;
        this.lastSpeechTime = Date.now();
        this.aiStartTime = 0;

        // Reset silence timer after AI finishes speaking
        // This ensures that if user remains silent after AI question, a new question will be generated
        this.clearSilenceTimer();

        // Grace period after AI finishes speaking (user may start answering immediately)
        setTimeout(() => {
          console.log("⏳ Grace period after AI finished speaking");
          this.schedulePostSpeechSilenceTimer('Silence after AI grace');
        }, 2000); // 2s grace so B1 users can think

        if (onEnd) onEnd();
      });
      this.isAISpeaking = true;
      this.aiResponsePending = true;
      this.aiStartTime = Date.now();
      this.callbacks.onStatusChange?.("AI is speaking...");
    } catch (error) {
      console.error('Audio playback error:', error);
      this.isAISpeaking = false;
      this.aiResponsePending = false;
      this.currentAudioElement = null;
      this.callbacks.onStatusChange?.("Listening...");
      this.aiStartTime = 0;

      // Reset silence timer after AI audio error
      // This ensures that if user remains silent after AI question fails, a new question will be generated
      this.clearSilenceTimer();
      this.schedulePostSpeechSilenceTimer('AI audio error recovery');

      if (onEnd) onEnd();
    }
  }

  private sendWebSocketMessage(message: any): boolean {
    if (message?.type === 'auth' && this.wsAuthPolicy.isTerminal()) {
      console.warn('⛔ Dropped auth send: invalid-auth is terminal');
      return false;
    }

    if (this.sessionId) {
      message.sessionId = this.sessionId;
    }

    const timestamp = Date.now();
    console.log(`📤 [${new Date().toISOString()}] Attempting to send message: ${message.type}`);

    if (this.websocket && this.websocket.readyState === WebSocket.OPEN) {
      try {
        const messageString = JSON.stringify(message);
        console.log(`📤 [${new Date().toISOString()}] Sending message:`, message);
        this.websocket.send(messageString);
        console.log(`✅ [${new Date().toISOString()}] Message sent successfully: ${message.type}`);
        
        // Track pending requests for monitoring
        if (message.requestId) {
          this.pendingRequests.set(message.requestId, { timestamp, type: message.type });
          console.log(`📋 [${new Date().toISOString()}] Tracking pending request: ${message.requestId} (${message.type})`);
        }
        
        this.lastWebSocketActivity = timestamp;
        return true;
      } catch (error) {
        console.error(`❌ [${new Date().toISOString()}] Failed to send WebSocket message:`, error);
        return false;
      }
    } else {
      console.warn(`⚠️ [${new Date().toISOString()}] WebSocket not connected, message dropped:`, {
        messageType: message.type,
        readyState: this.websocket?.readyState,
        websocketExists: !!this.websocket
      });
      return false;
    }
  }

  private startWebSocketHealthMonitoring(): void {
    // Clear any existing health check
    if (this.webSocketHealthCheckInterval) {
      clearInterval(this.webSocketHealthCheckInterval);
    }
    
    console.log("🔍 Starting WebSocket health monitoring (every 5 seconds)");
    
    this.webSocketHealthCheckInterval = setInterval(() => {
      const now = Date.now();
      const timeSinceLastActivity = now - this.lastWebSocketActivity;
      const pendingCount = this.pendingRequests.size;
      
      console.log(`🔍 [${new Date().toISOString()}] WebSocket Health Check:`, {
        connected: this.websocket?.readyState === WebSocket.OPEN,
        readyState: this.websocket?.readyState,
        timeSinceLastActivity: `${Math.round(timeSinceLastActivity / 1000)}s`,
        pendingRequests: pendingCount,
        isRecording: this.isRecording
      });
      
      // Check for stale pending requests (older than 30 seconds)
      const staleRequests: string[] = [];
      this.pendingRequests.forEach((request, requestId) => {
        if (now - request.timestamp > 30000) {
          staleRequests.push(requestId);
        }
      });
      
      if (staleRequests.length > 0) {
        console.warn(`⚠️ [${new Date().toISOString()}] Found ${staleRequests.length} stale pending requests:`, staleRequests);
        staleRequests.forEach(requestId => {
          const request = this.pendingRequests.get(requestId);
          console.warn(`⚠️ Stale request: ${requestId} (${request?.type}) - ${Math.round((now - request!.timestamp) / 1000)}s old`);
          this.pendingRequests.delete(requestId);
        });
      }
      
      // Check if WebSocket is still connected
      if (!this.websocket || this.websocket.readyState !== WebSocket.OPEN) {
        console.error(`❌ [${new Date().toISOString()}] WebSocket health check failed - not connected`);
        if (this.isRecording && this.shouldReconnect) {
          console.log("🔄 Scheduling reconnection due to health check failure");
          this.scheduleReconnect();
        }
      }
    }, 5000); // Check every 5 seconds
  }

  private clearReconnectTimer(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  private scheduleReconnect(): void {
    if (!this.shouldReconnect || this.wsAuthPolicy.isTerminal()) return;

    this.reconnectAttempts++;
    const delay = Math.min(1500 * Math.pow(2, this.reconnectAttempts - 1), 10000);

    this.clearReconnectTimer();
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      if (!this.shouldReconnect || this.wsAuthPolicy.isTerminal()) return;
      this.connectWebSocket().catch(console.error);
    }, delay);
  }

  cleanup(): void {
    // Mark task as ended to block any further AI responses
    this.taskEnded = true;

    this.stopRecording();
    this.shouldReconnect = false;
    this.clearReconnectTimer();
    this.wsAuthPolicy.markShutdown();
    this.clearLastPhaseTimeout();
    this.sessionId = null;
    this.fullTranscript = "";
    this.liveTranscript = "";

    // Clear WebSocket health monitoring
    if (this.webSocketHealthCheckInterval) {
      clearInterval(this.webSocketHealthCheckInterval);
      this.webSocketHealthCheckInterval = null;
    }

    // Clear pending requests
    this.pendingRequests.clear();
  }

  // New method to explicitly close the global audio context (e.g. when exiting the exam)
  public cleanupGlobalAudio(): void {
    try {
      const ctx: AudioContext | undefined = (window as any).__appAudioContext;
      if (ctx && typeof ctx.close === 'function' && ctx.state !== 'closed') {
        console.log('🛑 Explicitly closing global AudioContext');
        ctx.close().catch(() => { });
        (window as any).__appAudioContext = undefined;
      }
    } catch (e) {
      console.warn('Error closing global audio context:', e);
    }
  }
}
export default AudioService;
